<template>
  <div class="tag-waypoint" :class="clz" v-bind:style="wpPos">
    <div v-if="wp.time" class="tag-waypoint__time" @click.prevent="onClick">{{ wpIndex + 1 }}</div>
    <a v-if="isMoveable" class="tag-waypoint__dot" @click.prevent="onClick"></a>
    <!---->
    <div
      v-if="showBounds"
      ref="waypointBounds"
      class="tag-waypoint__bounds"
      v-bind:style="wpBounds"
      @mousedown="onMouseDown"
    >
      <div class="tag-waypoint__info-label">
        <span>Waypoint {{ wpLabel }}</span>
      </div>
      <preview-tag :tag="selection.tag" :bb="bb" class="tag-waypoint__tag-content"></preview-tag>
      <!---->
      <div v-if="isResizable">
        <tag-handle
          class="tag-waypoint__resizehandle left-top"
          v-on:dragging="onResize"
          v-on:startDrag="onStartResize"
          v-on:endDrag="onEndResize"
        >&nbsp;</tag-handle>
        <tag-handle
          class="tag-waypoint__resizehandle top-middle"
          :resizeMode="'height'"
          v-on:dragging="onResize"
          v-on:startDrag="onStartResize"
          v-on:endDrag="onEndResize"
        >&nbsp;</tag-handle>
        <tag-handle
          class="tag-waypoint__resizehandle left-middle"
          :resizeMode="'width'"
          v-on:dragging="onResize"
          v-on:startDrag="onStartResize"
          v-on:endDrag="onEndResize"
        >&nbsp;</tag-handle>
        <tag-handle
          class="tag-waypoint__resizehandle left-bottom"
          v-on:dragging="onResize"
          v-on:startDrag="onStartResize"
          v-on:endDrag="onEndResize"
        >&nbsp;</tag-handle>
        <tag-handle
          class="tag-waypoint__resizehandle right-bottom"
          v-on:dragging="onResize"
          v-on:startDrag="onStartResize"
          v-on:endDrag="onEndResize"
        >&nbsp;</tag-handle>
        <tag-handle
          class="tag-waypoint__resizehandle bottom-middle"
          :resizeMode="'height'"
          v-on:dragging="onResize"
          v-on:startDrag="onStartResize"
          v-on:endDrag="onEndResize"
        >&nbsp;</tag-handle>
        <tag-handle
          class="tag-waypoint__resizehandle right-top"
          v-on:dragging="onResize"
          v-on:startDrag="onStartResize"
          v-on:endDrag="onEndResize"
        >&nbsp;</tag-handle>
        <tag-handle
          class="tag-waypoint__resizehandle right-middle"
          :resizeMode="'width'"
          v-on:dragging="onResize"
          v-on:startDrag="onStartResize"
          v-on:endDrag="onEndResize"
        >&nbsp;</tag-handle>
      </div>
    </div>
    <!---->
    <div
      v-if="showBounds && hasMultipleWPs"
      class="tag-waypoint__controls"
      v-bind:style="controlsBounds"
    >
      <div
        v-if="wp.time >= 0"
        v-tooltip.right-start="'delete this waypoint'"
        class="tag-waypoint__delete"
        @click.stop="deleteWaypoint(wp)"
      >
        <i class="material-icons">clear</i>
      </div>
      <div
        v-if="wp.time >= 0"
        v-tooltip.right-start="'save this waypoint'"
        class="tag-waypoint__commit"
        @click.stop="onCommitWaypoint(wp)"
      >
        <i class="material-icons">done</i>
      </div>
      <div
        v-if="hasMultipleWPs && wp.time >= 0"
        v-tooltip.right-start="'copy this waypoint and save 1 sec later'"
        class="tag-waypoint__copy"
        @click.stop="onCopyWaypoint(wp)"
      >
        <i class="material-icons">content_copy</i>
      </div>
      <div
        v-if="isResizable && wp.time >= 0 && isConstrainProportions"
        v-tooltip.right-start="'lock proportions on resize'"
        class="tag-waypoint__lock"
        @click.stop="toggleConstrainProportions()"
      >
        <i class="material-icons">lock</i>
      </div>
      <div
        v-if="isResizable && wp.time >= 0 && !isConstrainProportions"
        v-tooltip.right-start="'lock proportions on resize'"
        class="tag-waypoint__lock-open"
        @click.stop="toggleConstrainProportions()"
      >
        <i class="material-icons">lock_open</i>
      </div>
      <div
        v-if="wp.time >= 0 && selection.tag.meta.imageSrc"
        class="tag-waypoint__photo-size"
        @click.stop="resizeImageNatural()"
      >
        <i class="material-icons">photo_size_select_actual</i>
      </div>
      <div
        v-if="wp.time >= 0 && selection.tag.meta.imageSrc"
        class="tag-waypoint__photo-size"
        @click.stop="resizeImageFullScreen()"
      >
        <i class="material-icons">zoom_out_map</i>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState, mapGetters } from "vuex";
import TagHandle from "./tagHandle";
import PreviewTag from "./previewTag";

const nonResizableTags = [
  "ptag",
  "generic",
  "quiz",
  "singleChoice",
  "logic",
  "filter",
  "marker"
];
const singleWPsTags = ["quiz", "singleChoice"];
const nonMoveable = ["quiz", "singleChoice", "logic", "filter", "marker"];

export default {
  name: "tag-waypoint",

  components: {
    "tag-handle": TagHandle,
    "preview-tag": PreviewTag
  },

  props: {
    scene: {
      type: Object
    },

    wp: {
      type: Object
    },

    wpIndex: {
      type: Number
    },

    bb: {
      type: Object
    },

    showBounds: {
      type: Boolean,
      default: false
    }
  },

  data: function() {
    return {
      x: this.wp.x,
      y: this.wp.y,
      editingState: "", // dragging || resizing || tracked
      isDraggingWaypoint: false,
      isConstrainProportions: false,
      ratio: 1,
      selectedTemplate: "",
      image: null,
      waypoints: []
    };
  },

  computed: {
    ...mapState(["selection", "video", "animationMode"]),
    ...mapGetters(["grid"]),

    isMoveable: function() {
      return nonMoveable.indexOf(this.selection.tag.template) < 0;
    },

    isResizable: function() {
      return nonResizableTags.indexOf(this.selection.tag.template) < 0;
    },

    hasMultipleWPs: function() {
      return singleWPsTags.indexOf(this.selection.tag.template) < 0;
    },

    constrainedProportions: function() {
      if (this.isConstrainProportions) {
        return this.image
          ? this.image.naturalWidth / this.image.naturalHeight
          : this.ratio;
      } else {
        return false;
      }
    },

    wpPos: function() {
      // TODO: needs to be in relation to track width / video length, fake 10
      // x and y are 0..1 in percent of width / height
      return {
        left: this.wp.x * 100 + "%",
        top: this.wp.y * 100 + "%"
      };
    },

    wpCalcBounds: function() {
      return {
        w: this.wp.pw * this.bb.width || this.wp.w,
        h: this.wp.ph * this.bb.height || this.wp.h
      };
    },

    wpBounds: function() {
      return {
        left: -this.wpCalcBounds.w / 2 + "px",
        top: -this.wpCalcBounds.h / 2 + "px",
        width: this.wpCalcBounds.w + "px",
        height: this.wpCalcBounds.h + "px",
        cursor: this.isMoveable ? "move" : "default"
      };
    },

    controlsBounds: function() {
      return {
        left: this.wp.x + this.wp.w / 2 + 10 + "px",
        top: this.wp.y - this.wp.h / 2 + "px"
      };
    },

    clz: function() {
      return {
        "is-dragging": this.editingState === "dragging",
        "is-tracked": this.wp.type === "tracked"
      };
    },

    numOfWaypoints: function() {
      return this.selection.scene && this.selection.scene.waypoints
        ? this.selection.scene.waypoints.length
        : 0;
    },

    wpLabel: function() {
      return (
        (this.selection.scene.waypoints || []).indexOf(
          this.selection.waypoint
        ) + 1
      );
      /*
      const t = Math.round(this.wp.time * 100) / 100
      const pw = Math.round(this.wp.pw * 100) / 100 // .toLocaleString('en-US', {minimumIntegerDigits: 1, useGrouping: false})
      const ph = Math.round(this.wp.ph * 100) / 100 // .toLocaleString('en-US', {minimumIntegerDigits: 1, useGrouping: false})
      return `t: ${t} w:${pw} h: ${ph}`
      */
    }
  },

  mounted() {
    // TODO: be nice, -> this is bad as f, but let's first sort out component nesting, need to get bounding box of interaction layer
    this.ilbb = document
      .querySelector(".editor__interaction-layer")
      .getBoundingClientRect();
    let elBounds = this.$refs.waypointBounds; // this.$el.querySelector('.tag-waypoint__bounds')

    if (elBounds) {
      this.wpbb = elBounds.getBoundingClientRect();
    } else {
      this.wpbb = {
        widht: 80,
        height: 80
      };
    }

    if (this.selection.tag && this.selection.tag.meta.imageSrc) {
      this.loadImage();
    }

    this.$watch(
      "selection.tag",
      () => {
        if (this.selection.tag && this.selection.tag.meta.imageSrc) {
          this.loadImage();
        }
        if (this.selectedTemplate === this.selection.tag.template) {
          return;
        }
        if (this.selection.tag && this.selection.tag.template === "imageTag") {
          this.isConstrainProportions = true;
        } else {
          this.isConstrainProportions = false;
        }
        this.selectedTemplate = this.selection.tag.template;
      },
      {
        deep: true
      }
    );
  },

  beforeDestroy() {
    // make sure no ghost global event listeners are kept
    document.removeEventListener("mousemove", this.onMouseMove);
    document.removeEventListener("mouseup", this.onMouseUp);
  },

  methods: {
    ...mapMutations(["deleteWaypoint", "commitWaypoint", "copyWaypoint"]),

    // handlers to resize a waypoint
    onStartResize: function() {
      this.$emit("editing");
      this.editingState = "resizing";
      this.rx = this.wp.x * this.ilbb.width;
      this.ry = this.wp.y * this.ilbb.height;
      this.rw = this.wp.w;
      this.rh = this.wp.h;
    },

    // handlers to move a waypoint
    onEndResize: function() {
      this.$emit("resize");
      this.editingState = "";
    },

    loadImage: function() {
      if (
        !this.image ||
        this.image.lang !== this.selection.tag.meta.imageSrc.lang
      ) {
        const _this = this;
        let imageSrc = "";
        if (typeof this.selection.tag.meta.imageSrc === "string") {
          imageSrc = this.selection.tag.meta.imageSrc;
        } else {
          imageSrc = this.selection.tag.meta.imageSrc[
            this.selection.tag.meta.imageSrc.lang
          ];
        }
        const newImage = new Image();
        newImage.src = imageSrc;
        newImage.onload = event => {
          _this.image =
            event.path && event.path.length > 0 ? event.path[0] : null;
          if (_this.image) {
            _this.image.lang = _this.selection.tag.meta.imageSrc.lang;
          }
        };
      }
    },

    // handlers for resize
    onResize: function(ev) {
      ev = this.handleKeyModifier(ev);
      let left = ev.cx - this.ilbb.left;
      let top = ev.cy - this.ilbb.top;
      let w = 0;

      if (ev.resizeMode && ev.resizeMode === "height") {
        w = this.wp.w;
      } else {
        w = Math.abs(this.rx - left) * 2;
      }

      let h = 0;

      if (ev.resizeMode && ev.resizeMode === "width") {
        h = this.wp.h;
      } else {
        h = Math.abs(this.ry - top) * 2;
      }

      this.resizeBounds(w, h, ev.resizeMode);
    },

    handleKeyModifier(ev) {
      if (ev.altKey) {
        ev.cx = Math.round(ev.cx / this.grid.size) * this.grid.size;
        ev.cy = Math.round(ev.cy / this.grid.size) * this.grid.size;
      }
      return ev;
    },

    // when a non editing waypoint is click notify parent listener
    onClick: function(ev) {
      this.$emit("click", ev);
    },

    // start dragging the waypoint box
    onMouseDown: function() {
      if (!this.isMoveable) {
        return null;
      }
      this.ilbb = document
        .querySelector(".editor__interaction-layer")
        .getBoundingClientRect();
      this.$emit("editing");
      this.isDraggingWaypoint = true;
      document.addEventListener("mousemove", this.onMouseMove);
      document.addEventListener("mouseup", this.onMouseUp);
    },

    // move the waypoint box
    onMouseMove: function(ev) {
      const waypoints = this.selection.scene.waypoints;
      if (this.isDraggingWaypoint) {
        let cx = ev.clientX;
        let cy = ev.clientY;

        if (ev.altKey) {
          cx = Math.round(cx / this.grid.size) * this.grid.size;
          cy = Math.round(cy / this.grid.size) * this.grid.size;
        }

        const x = (cx - this.ilbb.left) / this.ilbb.width;
        const y = (cy - this.ilbb.top) / this.ilbb.height;
        if (this.animationMode === false) {
          for (let i = 0; i < waypoints.length; i++) {
            let wp = waypoints[i];
            if (
              wp.x === this.wp.x &&
              wp.y === this.wp.y &&
              this.selection.waypoint.id !== wp.id
            ) {
              wp.x = x;
              wp.y = y;
            }
          }
        }
        this.wp.x = x;
        this.wp.y = y;
      }
    },

    // clean up after dragging waypoint box
    onMouseUp: function() {
      // ev.stopImmediatePropagation()
      this.isDraggingWaypoint = false;
      document.removeEventListener("mousemove", this.onMouseMove);
      document.removeEventListener("mouseup", this.onMouseUp);
    },

    onCommitWaypoint: function(wp) {
      this.commitWaypoint(wp);
    },

    onCopyWaypoint: function(wp) {
      // FIX:: Copy Inital Waypoint
      // was just copy Waypoint
      this.commitWaypoint(wp);
      this.copyWaypoint(wp);
    },

    toggleConstrainProportions: function() {
      this.isConstrainProportions = !this.isConstrainProportions;
      this.ratio = this.wp.w / this.wp.h;
    },

    resizeImageNatural: function() {
      if (this.selection.tag.meta.imageSrc) {
        this.resizeBounds(this.image.naturalWidth, this.image.naturalHeight);
      }
    },

    resizeImageFullScreen: function() {
      const dimensions = this.video.dimensions;

      if (this.selection.tag.meta.imageSrc) {
        this.resizeBounds(dimensions.width, dimensions.height, true, 0.5, 0.5);
      }
    },

    resizeBounds: function(width, height, resizeMode, x, y) {
      const minSize = 32;
      const waypoints = this.selection.scene.waypoints;

      if (!resizeMode) {
        height = this.constrainedProportions
          ? width / this.constrainedProportions
          : height;
      }

      const w = width > minSize ? width : minSize;
      const h = height > minSize ? height : minSize;
      const pw = this.wp.w / this.bb.width;
      const ph = this.wp.h / this.bb.height;
      x = x ? x : this.wp.x;
      y = y ? y : this.wp.y;

      if (!this.animationMode) {
        for (let i = 0; i < waypoints.length; i++) {
          let wp = waypoints[i];
          if (
            wp.w === this.wp.w &&
            wp.h === this.wp.h &&
            wp.pw === this.wp.pw &&
            wp.ph === this.wp.ph &&
            wp.x === this.wp.x &&
            wp.y === this.wp.y &&
            this.selection.waypoint.id !== wp.id
          ) {
            wp.w = w;
            wp.h = h;
            wp.pw = pw;
            wp.ph = ph;
            wp.x = x;
            wp.y = y;
          }
        }
      }

      this.wp.w = w;
      this.wp.h = h;
      this.wp.pw = pw;
      this.wp.ph = ph;
      this.wp.x = x;
      this.wp.y = y;
    }
  }
};
</script>
